import type { FC } from 'react';
import { AlertOctagon } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';

import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { num, optStr, str } from '@/app/lib/a2ui/props.ts';

const messages = defineMessages({
  heading: {
    id: 'safetyStrip.heading',
    defaultMessage: 'Long waiting period — read before you spray',
    description: 'Heading of the red warning band shown for products with a long pre-harvest interval.',
  },
  days: {
    id: 'safetyStrip.days',
    defaultMessage: '{days} days before harvest',
    description: 'Number of days that must pass between this spray and harvesting.',
  },
});

export const SafetyStrip: FC<A2uiNodeViewProps> = ({ node }) => {
  const intl = useIntl();
  const product = str(node.props.product);
  const phiDays = Math.round(num(node.props.phiDays) ?? 0);
  const line = str(node.props.line);
  const residueNote = optStr(node.props.residueNote);

  return (
    <div
      role="alert"
      className="w-full rounded-lg border-2 border-destructive bg-destructive p-5 text-destructive-foreground"
    >
      <p className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-caps">
        <AlertOctagon className="size-5 shrink-0" aria-hidden />
        {intl.formatMessage(messages.heading)}
      </p>
      <p className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-lg font-bold tracking-tight">
        <span translate="no">{product}</span>
        <span className="tabular-nums text-destructive-foreground/80">
          {intl.formatMessage(messages.days, { days: phiDays })}
        </span>
      </p>
      <p className="text-base font-medium leading-snug">{line}</p>
      {residueNote ? (
        <p className="mt-2 text-sm leading-snug text-destructive-foreground/80">{residueNote}</p>
      ) : null}
    </div>
  );
};
